import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { GET_TAXONOMY_REVIEW_QUERY_KEY } from "../../utils/constants/queryKeys";
import {
  DEFAULT_TAB,
  REVIEW_FILTERS,
  REVIEW_TABS,
} from "../../utils/constants/AdIntelligence";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import useUrlPaging from "../../hooks/useUrlPaging";
import CreativeAnalysisAPIs from "../../apiRoutes/CreativeAnalysis";
import SectionHeader from "../common/SectionHeader";
import ReviewFilterBar from "./ReviewFilterBar";
import ReviewTable from "./ReviewTable";
import ReviewTabs from "./ReviewTabs";
import TagReviewDialog from "./TagReviewDialog";

/** Taxonomy tags on launched ads: browse them, and approve or correct the ones you review. */
const AdIntelligence = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(() => {
    const t = searchParams.get("tab");
    return REVIEW_TABS.some((x) => x.key === t) ? t : DEFAULT_TAB;
  });
  const [filters, setFilters] = useState(null);
  const [selected, setSelected] = useState(null);
  const lastScope = useRef(null);
  const axiosPrivate = useAxiosPrivate();
  const queryClient = useQueryClient();
  const { page, setPage, pageSize, setPageSize } = useUrlPaging();
  const filter = REVIEW_FILTERS[tab].some(
    (f) => f.value === filters?.apiParams.filter
  )
    ? filters.apiParams.filter
    : undefined;
  const reviewedBy =
    tab === "reviewed" ? filters?.apiParams.reviewed_by : undefined;
  const category = filters?.apiParams.category;
  const dateFrom = filters?.apiParams.date_from;
  const dateTo = filters?.apiParams.date_to;
  const { data: result, isLoading } = useQuery({
    queryKey: [
      ...GET_TAXONOMY_REVIEW_QUERY_KEY,
      tab,
      filter,
      reviewedBy,
      category,
      dateFrom,
      dateTo,
      page,
      pageSize,
    ],
    queryFn: () =>
      CreativeAnalysisAPIs.handleListReviewTags({
        axiosPrivate,
        tab,
        filter,
        category,
        reviewed_by: reviewedBy,
        date_from: dateFrom,
        date_to: dateTo,
        limit: pageSize,
        offset: page * pageSize,
      }),
    enabled: !!filters,
    placeholderData: keepPreviousData,
  });
  const rows = result?.data ?? [];
  const selectedIndex = selected
    ? rows.findIndex((r) => r.id === selected.id)
    : -1;
  const nextRow = selectedIndex >= 0 ? rows[selectedIndex + 1] : null;

  const handleFiltersChange = useCallback((next) => setFilters(next), []);

  useEffect(() => {
    if (!filters) return;
    const scope = `${tab}|${filters.key}`;
    if (lastScope.current !== null && lastScope.current !== scope) setPage(0);
    lastScope.current = scope;
  }, [tab, filters, setPage]);

  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (tab !== DEFAULT_TAB) next.set("tab", tab);
        else next.delete("tab");
        return next;
      },
      { replace: true }
    );
  }, [tab, setSearchParams]);

  const handleReviewed = () => {
    setSelected(nextRow || null);
    queryClient.invalidateQueries({ queryKey: GET_TAXONOMY_REVIEW_QUERY_KEY });
  };

  return (
    <section className="p-4 md:p-6">
      <div className="mb-5">
        <SectionHeader title="Ad Intelligence" />
        <p className="mt-1 text-sm text-gray-500">
          The taxonomy tags on launched ads. Review the ones you want: approve
          the right ones and correct the wrong ones.
        </p>
      </div>
      <div className="flex min-h-[calc(100vh-200px)] flex-col overflow-hidden rounded-xl border border-gray-200 bg-white">
        <ReviewTabs
          tab={tab}
          counts={result?.tabCounts}
          onSelect={(key) => {
            setTab(key);
            setSelected(null);
          }}
        />
        <ReviewFilterBar
          tab={tab}
          filterCounts={result?.filterCounts}
          onChange={handleFiltersChange}
        />
        <ReviewTable
          tab={tab}
          rows={rows}
          isLoading={isLoading || !filters}
          totalCount={result?.count ?? 0}
          page={page}
          setPage={setPage}
          pageSize={pageSize}
          onPageSizeChange={(n) => {
            setPageSize(n);
            setPage(0);
          }}
          activeId={selected?.id}
          onOpen={setSelected}
        />
      </div>
      {selected && (
        <TagReviewDialog
          key={selected.id}
          tag={selected}
          queue={filter || tab}
          canApprove={tab !== "reviewed"}
          hasNext={!!nextRow}
          onClose={() => setSelected(null)}
          onReviewed={handleReviewed}
        />
      )}
    </section>
  );
};

export default AdIntelligence;
